import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  SNOOZE_OPTIONS,
  conversationHref,
  copyNumber,
  customerHref,
  waplifyChatHref,
  type SnoozeDuration,
} from "./api";
import {
  callUsage,
  daysUntil,
  fetchCustomer,
  fetchSubscriptions,
  formatAge,
  formatDate,
  pickSubscription,
} from "./customer";
import type { WorklistItem } from "./priority";
import { dangerBtn, focusRing, ghostBtn, microLabel, pillTone, primaryBtn } from "./ui";

/**
 * Customer-360 — the panel that makes the queue self-sufficient.
 *
 * Before this, a care manager could see WHO was at risk and WHY, but had to leave the
 * queue (and lose their place) to understand the customer. Now: click a row -> the
 * whole picture slides in -> act -> close -> next row.
 *
 * SAFETY: this panel carries the SAME actions as the row and NOTHING more. Every one
 * is an internal DB state change or a navigation. Nothing here can send a WhatsApp
 * message, an SMS, a push, or place a call. "Message on WhatsApp" is an <a> that only
 * OPENS a chat window — a human types and sends it there.
 */

/** Focusables inside the dialog, in DOM order — the tab ring. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/* ---------- layout primitives ---------- */

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-clinic-200 px-5 py-4 dark:border-navy-700">
      <h3 className={`mb-2.5 ${microLabel}`}>{title}</h3>
      {children}
    </section>
  );
}

/** Label left, value right — a spec sheet, not a form. Numbers always tabular. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 text-xs text-clinic-500 dark:text-gray-400">
        {label}
      </span>
      <span className="min-w-0 text-right text-[13px] font-medium tabular-nums text-clinic-900 dark:text-white">
        {value}
      </span>
    </div>
  );
}

/** Per-section "no data" — the panel states an absence, it never fakes a value. */
function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="text-[13px] text-clinic-400 dark:text-gray-500">{children}</p>
  );
}

function Skeleton() {
  return (
    <div className="px-5 py-4" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="mb-5">
          <div className="mb-3 h-2.5 w-24 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
          <div className="mb-2 h-3.5 w-full animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
          <div className="h-3.5 w-2/3 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
        </div>
      ))}
    </div>
  );
}

/* ---------- call utilisation ---------- */

/**
 * The churn tell. A customer paying for 8 calls who has taken 2 is not "fine" — they
 * are about to lapse having felt no value. The bar is deliberately NOT a severity
 * colour by default: it only goes warn when utilisation is actually poor.
 */
function CallMeter({
  used,
  max,
  pct,
  underused,
}: {
  used: number;
  max: number;
  pct: number;
  underused: boolean;
}) {
  return (
    <div className="mt-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-clinic-500 dark:text-gray-400">
          Calls used
        </span>
        <span className="text-[13px] font-semibold tabular-nums text-clinic-900 dark:text-white">
          {used} <span className="text-clinic-400 dark:text-gray-500">/ {max}</span>
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label="Calls used out of plan allowance"
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-clinic-200 dark:bg-navy-700"
      >
        <div
          className={`h-full rounded-full ${
            underused ? "bg-warn-400" : "bg-action-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {underused && (
        <p
          data-testid="c360-underused"
          className="mt-1.5 text-xs font-medium text-warn-700 dark:text-amber-400"
        >
          Only {pct}% of the plan used — low value felt, high churn risk.
        </p>
      )}
    </div>
  );
}

/* ---------- panel ---------- */

interface Props {
  item: WorklistItem;
  onClose: () => void;
  /** P2 only — a P0 conversation has no "done" state we own. */
  onMarkDone?: (item: WorklistItem) => void;
  onSnooze: (item: WorklistItem, duration: SnoozeDuration) => void;
  pending: boolean;
  error: string | null;
}

export default function Customer360({
  item,
  onClose,
  onMarkDone,
  onSnooze,
  pending,
  error,
}: Props) {
  const headingId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const [shown, setShown] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch ON OPEN — per opened customer, never per row. The queue can hold 50 rows; we
  // do not fetch 50 profiles to render a list. The two requests go out in parallel.
  const q = useQuery({
    queryKey: ["worklist", "customer", item.mobile],
    queryFn: () => fetchCustomer(item.mobile),
    staleTime: 30_000,
  });
  const subs = useQuery({
    queryKey: ["worklist", "subscriptions", item.mobile],
    queryFn: () => fetchSubscriptions(item.mobile),
    staleTime: 30_000,
  });

  const now = Date.now();
  const data = q.data ?? null;
  const sub = pickSubscription(subs.data ?? []);
  const usage = callUsage(sub);
  const patients = data?.patients ?? [];
  const appointments = data?.appointment ?? [];
  const lastAppt = appointments[0] ?? null;

  /**
   * The queue's chat-list only carries a short display name ("Priya"); the customer
   * record has the real one ("Priya Sharma"). Prefer the fetched name once it lands —
   * a 360 that shows LESS identity than the row it opened from is a broken promise.
   * Falls back to the row's name while loading, and if there is no record at all.
   */
  const fullName = data?.user_name || item.name;

  const profileHref = customerHref(item.mobile);
  const convoHref = conversationHref(item.kind, item.sourceId, item.mobile);
  const waHref = item.tier === "p2" ? waplifyChatHref(item.mobile) : null;

  // Slide in on the frame after mount so the transition actually runs (a node born at
  // its final position never transitions). Transform/opacity only — no layout thrash.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Focus goes INTO the dialog on open, so a keyboard CM is never left behind on the
  // row with an open panel they cannot reach.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  /** Esc closes; Tab is trapped inside the dialog (it is modal — nothing behind it). */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => n.offsetParent !== null
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      // Wrap at the ends, and pull focus back in if it has somehow escaped.
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  const onWhatsApp = useCallback(() => {
    void copyNumber(item.mobile).then(setCopied);
  }, [item.mobile]);

  const days = daysUntil(sub?.s_end_time, now);

  // The panel is only as trustworthy as its weakest request — treat the pair as one.
  const isLoading = q.isLoading || subs.isLoading;
  const isError = q.isError || subs.isError;
  const retry = () => {
    void q.refetch();
    void subs.refetch();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop: click closes. aria-hidden — the dialog below is the only content. */}
      <div
        aria-hidden="true"
        data-testid="c360-backdrop"
        onClick={onClose}
        className={`absolute inset-0 bg-clinic-900/40 transition-opacity duration-200 motion-reduce:transition-none ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="customer-360"
        onKeyDown={onKeyDown}
        className={`relative flex h-full w-[440px] max-w-full flex-col overflow-y-auto overscroll-contain bg-white shadow-2xl transition-transform duration-200 ease-out motion-reduce:transition-none dark:!bg-navy-800 ${
          shown ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* ---------- header: who, and why they are in the queue ---------- */}
        <header className="sticky top-0 z-10 border-b border-clinic-200 bg-white px-5 pb-4 pt-4 dark:border-navy-700 dark:!bg-navy-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={microLabel}>Customer</p>
              <h2
                id={headingId}
                data-testid="c360-name"
                className="mt-1 text-lg font-bold leading-tight tracking-tight text-clinic-900 dark:text-white"
              >
                {fullName}
              </h2>
              <p className="mt-0.5 text-[13px] tabular-nums text-clinic-500 dark:text-gray-400">
                {item.mobile}
                {sub?.plan_name ? (
                  <>
                    <span aria-hidden="true"> · </span>
                    <span className="font-medium text-clinic-700 dark:text-gray-200">
                      {sub.plan_name}
                    </span>
                  </>
                ) : null}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              {/* Expand to the full record — the panel is a summary, not a cage. */}
              {profileHref && (
                <Link
                  to={profileHref}
                  data-testid="c360-expand"
                  title="Open the full customer record"
                  className={ghostBtn}
                >
                  Expand <span aria-hidden="true">↗</span>
                </Link>
              )}
              <button
                ref={closeRef}
                type="button"
                data-testid="c360-close"
                aria-label="Close customer panel"
                onClick={onClose}
                className={`rounded-md px-2 py-1 text-lg leading-none text-clinic-500 transition-colors hover:bg-clinic-100 hover:text-clinic-900 motion-reduce:transition-none dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white ${focusRing}`}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>

          {/* The signal that put them here — the SAME pill they clicked on the row. */}
          <span
            data-testid="c360-pill"
            className={`mt-2.5 inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${pillTone[item.severity]}`}
          >
            {item.pill}
          </span>
        </header>

        {/* ---------- why they're here (from the queue, always available) ---------- */}
        <Section title="Why they're here">
          <p className="text-[13px] leading-snug text-clinic-700 dark:text-gray-200">
            {item.reason}
          </p>
          <p
            data-testid="c360-signal"
            className="mt-1 text-xs tabular-nums leading-snug text-clinic-500 dark:text-gray-400"
          >
            {item.meta}
          </p>
        </Section>

        {/* ---------- the fetched 360 ---------- */}
        {isLoading && <Skeleton />}

        {!isLoading && isError && (
          <div role="alert" className="border-t border-clinic-200 px-5 py-5 text-center dark:border-navy-700">
            <p className="text-sm font-medium text-clinic-900 dark:text-white">
              Couldn&rsquo;t load this customer.
            </p>
            <p className="mt-1 text-xs text-clinic-500 dark:text-gray-400">
              The queue signal above is still accurate — only the profile failed.
            </p>
            <button
              type="button"
              data-testid="c360-retry"
              onClick={retry}
              className={`mt-3 ${primaryBtn}`}
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && !data && (
          <Section title="Customer record">
            <Empty>
              No customer record on file for this number. The queue signal above still
              stands — open the conversation to work it.
            </Empty>
          </Section>
        )}

        {!isLoading && !isError && data && (
          <>
            <Section title="Subscription">
              {sub ? (
                <>
                  <Field label="Plan" value={sub.plan_name || "Not on file"} />
                  <Field
                    label="Status"
                    value={
                      <span
                        className={
                          sub.is_active
                            ? "text-clinic-900 dark:text-white"
                            : "text-crit-600 dark:text-red-400"
                        }
                      >
                        {sub.is_active ? "Active" : "Expired"}
                      </span>
                    }
                  />
                  <Field
                    label="Started"
                    value={formatDate(sub.s_start_time) ?? "Not on file"}
                  />
                  <Field
                    label={days !== null && days < 0 ? "Lapsed on" : "Ends"}
                    value={formatDate(sub.s_end_time) ?? "Not on file"}
                  />
                  {days !== null && (
                    <Field
                      label={days < 0 ? "Lapsed" : "Remaining"}
                      value={
                        <span
                          data-testid="c360-days"
                          className={
                            days < 0
                              ? "text-crit-600 dark:text-red-400"
                              : days <= 2
                              ? "text-warn-600 dark:text-amber-400"
                              : undefined
                          }
                        >
                          {days < 0
                            ? `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`
                            : days === 0
                            ? "Expires today"
                            : `${days} day${days === 1 ? "" : "s"}`}
                        </span>
                      }
                    />
                  )}

                  {usage ? (
                    <CallMeter {...usage} />
                  ) : (
                    <p className="mt-2 text-xs text-clinic-400 dark:text-gray-500">
                      Call allowance not on file for this plan.
                    </p>
                  )}
                </>
              ) : (
                <Empty>No subscription on file.</Empty>
              )}
            </Section>

            <Section title="Family">
              {patients.length > 0 ? (
                <ul className="flex list-none flex-col gap-2">
                  {patients.map((p, i) => {
                    const age = formatAge(p.date_of_birth, now);
                    const meta = [
                      age,
                      p.gender,
                      p.weight && p.weight !== "NA" ? p.weight : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <li
                        key={p.id ?? i}
                        data-testid="c360-patient"
                        className="flex items-baseline justify-between gap-3"
                      >
                        <span className="text-[13px] font-semibold text-clinic-900 dark:text-white">
                          {p.name || "Unnamed child"}
                        </span>
                        <span className="text-xs tabular-nums text-clinic-500 dark:text-gray-400">
                          {meta || "No details on file"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <Empty>No children on file.</Empty>
              )}
            </Section>

            <Section title="Engagement">
              <Field
                label="Member since"
                value={formatDate(data.user_joining_date) ?? "Not on file"}
              />
              <Field
                label="Questions asked"
                value={data.total_questions ?? 0}
              />
              <Field
                label="Messages"
                value={data.total_messages ?? 0}
              />
              {/*
                There is deliberately NO "last active" row. picard's Account.last_login is
                declared auto_now_add and nothing ever writes to it, so it is account
                CREATION time wearing a misleading name. Rendering it would be a lie. The
                last consultation below is the honest activity signal we do have.
              */}
              <Field
                label="Last consultation"
                value={
                  lastAppt
                    ? formatDate(lastAppt.appointment_time) ?? "Date not on file"
                    : "None yet"
                }
              />
              {data.current_groups && data.current_groups.length > 0 && (
                <Field
                  label="Groups"
                  value={data.current_groups.join(", ")}
                />
              )}
            </Section>
          </>
        )}

        {/* ---------- actions: the row's actions, so the CM never has to close ---------- */}
        <footer className="sticky bottom-0 mt-auto border-t border-clinic-200 bg-white px-5 py-3 dark:border-navy-700 dark:!bg-navy-800">
          {error && (
            <p
              role="alert"
              data-testid="c360-error"
              className="mb-2 rounded bg-crit-100 px-2 py-1 text-xs font-medium text-crit-700 ring-1 ring-crit-200"
            >
              {error} — nothing changed. Retry, or reload the queue.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            {convoHref && (
              <Link
                to={convoHref}
                data-testid="c360-open-conversation"
                className={primaryBtn}
              >
                Open conversation
              </Link>
            )}

            {/*
              An ANCHOR, not a button — it navigates, it does not act. It opens waplify's
              chat UI in a new tab where a HUMAN writes and sends the message. Nothing in
              this panel can put a message on the wire. Never rendered on P0: a P0 is an
              unanswered MEDICAL question for a doctor to answer in-app.
            */}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="c360-whatsapp"
                onClick={onWhatsApp}
                className={primaryBtn}
              >
                Message on WhatsApp
              </a>
            )}

            {/* Mark done closes a real retention task — destructive, so it is two-step. */}
            {onMarkDone &&
              (confirming ? (
                <span
                  role="group"
                  aria-label="Confirm mark done"
                  className="flex items-center gap-1.5 rounded-md bg-warn-50 px-2 py-1 ring-1 ring-warn-200 dark:!bg-navy-900 dark:ring-navy-700"
                >
                  <span className="text-xs font-medium text-warn-700 dark:text-amber-200">
                    Close task?
                  </span>
                  <button
                    type="button"
                    data-testid="c360-mark-done-confirm"
                    disabled={pending}
                    onClick={() => {
                      setConfirming(false);
                      onMarkDone(item);
                    }}
                    className={dangerBtn}
                  >
                    {pending ? "Working…" : "Yes, mark done"}
                  </button>
                  <button
                    type="button"
                    data-testid="c360-mark-done-cancel"
                    onClick={() => setConfirming(false)}
                    className={ghostBtn}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  data-testid="c360-mark-done"
                  disabled={pending}
                  onClick={() => setConfirming(true)}
                  className={ghostBtn}
                >
                  Mark done
                </button>
              ))}

            {/* Snooze is a plain row of choices here — the panel has the width for it,
                and a popup menu inside a modal is a focus-trap fight nobody needs. */}
            <span
              role="group"
              aria-label="Snooze"
              className="flex items-center gap-1.5"
            >
              <span className="text-xs text-clinic-500 dark:text-gray-400">
                Snooze
              </span>
              {SNOOZE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`c360-snooze-${opt.value}`}
                  disabled={pending}
                  onClick={() => onSnooze(item, opt.value)}
                  className={ghostBtn}
                >
                  {opt.label}
                </button>
              ))}
            </span>
          </div>

          <span aria-live="polite" className="sr-only">
            {copied ? "Number copied to clipboard" : ""}
          </span>
        </footer>
      </div>
    </div>
  );
}
